import { Component, OnInit } from '@angular/core';
import {
  Chart,
  ChartConfiguration,
  ChartOptions,
  registerables,
} from 'chart.js';
import * as saveAs from 'file-saver';
import { DashboardKpiDTO, DashboardKPIs } from '../models/dashboard-kpis.model';
import { DashboardService } from '../services/dashboard.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgChartsModule } from 'ng2-charts';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-plugin-trendline';

Chart.register(...registerables);
Chart.register(ChartDataLabels);

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, NgChartsModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  kpi!: DashboardKpiDTO;

  dailySalesData: any;
  monthlySalesData: any;
  commissionCountPie: any;
  commissionAmountPie: any;
  chartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      datalabels: {
        color: '#000',
        anchor: 'center',
        align: 'center',
        formatter: (value: any) => '₹' + value,
        font: {
          weight: 'bold',
          size: 12,
        },
      },
    },
  };
  chartOptionsBar: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top' as const,
        display: false,
      },
      datalabels: {
        color: '#000',
        anchor: 'end',
        align: 'end',
        formatter: (value: any) => {
          return '₹' + this.formatIndian(value); // ✅ Two decimals
        },
        font: {
          weight: 'bold',
          size: 12,
        },
      },
    },
  };
  chartOptionsLine: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top' as const,
        display: false,
      },
      datalabels: {
        color: '#000',
        anchor: 'end',
        align: 'end',
        formatter: (value: any) => {
          return '₹' + this.formatIndian(value); // ✅ Two decimals
        },
        font: {
          weight: 'bold',
          size: 12,
        },
      },
    },
  };
  chartOptionsPie: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      datalabels: {
        color: '#000',
        anchor: 'end',
        align: 'end',
        formatter: (value: any) => {
          return this.formatIndian(value); // ✅ Two decimals
        },
        font: {
          weight: 'bold',
          size: 12,
        },
      },
    },
  };

  constructor(private dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  loadDashboard() {
    this.dashboardService.getDashboardKpi().subscribe((resp) => {
      this.kpi = resp;

      // Prepare Daily Chart Data
      this.dailySalesData = {
        labels: resp.dailySalesLabels,
        datasets: [
          {
            label: 'Daily Sales',
            data: resp.dailySalesValues,
            tension: 0.3,
            fill: true,
            borderColor: 'skyblue',
            backgroundColor: 'lightblue',
            borderWidth: 2,
            trendlineLinear: {
              style: 'rgba(255, 0, 0, 0.86)',
              lineStyle: 'dotted',
              width: 2,
            },
          },
        ],
      };

      // Prepare Monthly Chart Data
      this.monthlySalesData = {
        labels: resp.monthlySalesLabels,
        datasets: [
          {
            label: 'Monthly Sales',
            data: resp.monthlySalesValues,
            backgroundColor: this.generateColors(
              resp.monthlySalesValues.length
            ),
            borderColor: this.generateColors(resp.monthlySalesValues.length),
            borderWidth: 1,
          },
        ],
      };

      // Pie Chart: Commission Count by Status
      this.commissionCountPie = {
        labels: Object.keys(resp.commissionCountByStatus),
        datasets: [
          {
            data: Object.values(resp.commissionCountByStatus),
          },
        ],
      };

      // Pie Chart: Commission Amount by Status
      this.commissionAmountPie = {
        labels: Object.keys(resp.commissionAmountByStatus),
        datasets: [
          {
            data: Object.values(resp.commissionAmountByStatus),
          },
        ],
      };
    });
  }

  generateColors(count: number): string[] {
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(`hsl(${Math.random() * 360}, 70%, 60%)`);
    }
    return colors;
  }
  formatIndian = (value: number): string => {
    return Math.round(value).toLocaleString('en-IN');
  };
}
